import React, { useCallback, useEffect, useState } from 'react';
import * as dateFns from 'date-fns';
import { Dropdown } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import {
  getAllDateRanges,
  getCurrentSeasonYear,
  getTeamData,
  isIn,
  isJson,
  routeToSpecificScope,
  YYYYMMDDToDate
} from '../../../../helpers';
import {
  ALL_SEASON_MONTHS,
  ALL_SEASON_YEAR,
  CUSTOM_LABEL,
  FILTER_GLOBAL_KEYS,
  FILTER_KEYS,
  OFF_SEASON_MONTHS,
  YEARS
} from '../../../../config/constants';
import { ParamWrapper } from '../ParamWrapper';
import { useFiltersState } from '../../../../store/hooks';
import { YearSeasonFilterDataType, YearSeasonFilterPropsType, YearSeasonFilterWrappedPropsType } from './types';
import { Month, Week } from '../../../../store/types';
import { RangeDate, RangeYYMMDDDDate } from '../../../../config/commonTypes';

import './styles.scss';
import { useRouter } from 'helpers/customHooks';

const YearSeasonFilterWrapped = ({
                                   changeFilterDataWrapped, // from wrapper
                                   forceScope, show, close, open, defaultYear, onlySeasonWeeks, isNcaa
                                 }: YearSeasonFilterWrappedPropsType) => {

  const { pathname } = useLocation();
  const specificScope = forceScope || routeToSpecificScope(pathname);
  const {
    [FILTER_GLOBAL_KEYS.WEEKS]: weeks = {},
    [FILTER_KEYS.DATE_RANGE]: filterData = {
      selectedDates: [],
      year: '',
      week: CUSTOM_LABEL,
      default: CUSTOM_LABEL
    } as YearSeasonFilterDataType<RangeDate>
  } = useFiltersState(specificScope, FILTER_GLOBAL_KEYS.WEEKS, FILTER_KEYS.DATE_RANGE);
  const [newData, setNewData] = useState<YearSeasonFilterDataType<RangeYYMMDDDDate>>({
    selectedDates: [],
    default: CUSTOM_LABEL,
    year: '',
    week: CUSTOM_LABEL,
  });
  const [appliqued, setAppliqued] = useState(false);
  const [inSeasonAll, setInSeasonAll] = useState(false);
  const [allMonthAll, setAllMonthAll] = useState(false);
  const [offSeasonAll, setOffSeasonAll] = useState(false);
  const [changeToNcaa, setChangeToNcaa] = useState(false);
  const [changeToNfl, setChangeToNfl] = useState(false);
  useEffect(()=>{
    if(isNcaa){
      setChangeToNcaa(true);
      setChangeToNfl(false);
    }else{
      setChangeToNfl(true);
      setChangeToNcaa(false);
    }
  },[isNcaa]);
  const isFullYearRanges = useCallback((dateRanges, year) => {
    const isInSeasonAll = ((weeks && weeks[year]) || []).every((week: Week) => (isIn(dateRanges, {
      start: week.startOfWeek,
      end: week.endOfWeek
    })));
    const isOffSeasonAll = (OFF_SEASON_MONTHS[year] || []).every((month: Month) => (isIn(dateRanges, {
      start: month.startOfMonth,
      end: month.endOfMonth
    })));
    const isAllMonthAll = (ALL_SEASON_MONTHS[year] || []).every((month: Month) => (isIn(dateRanges, {
      start: month.startOfMonth,
      end: month.endOfMonth
    })));
    return [isInSeasonAll, isOffSeasonAll, isAllMonthAll];
  }, [weeks]);
  const getSelectedData = useCallback((clear?: boolean): YearSeasonFilterDataType<RangeYYMMDDDDate> => {
    let initialRangeDates = (filterData.selectedDates && !clear) ?
      filterData.selectedDates.map((range: RangeDate) => ({
        start: dateFns.format(new Date(range.start), 'yyyy-MM-dd'),
        end: dateFns.format(new Date(range.end), 'yyyy-MM-dd')
      })) : [];
    let year = filterData.year;
    if (!year || clear) {
      year = defaultYear ? defaultYear : getCurrentSeasonYear(!!isNcaa);
    }
    if (!filterData.year || clear) {
      initialRangeDates = getAllDateRanges(year as number, weeks, !!isNcaa, !onlySeasonWeeks);
    }
    const [isInSeasonAll, isOffSeasonAll, isAllMonthAll] = isFullYearRanges(initialRangeDates, year);
    setInSeasonAll(isInSeasonAll);
    setOffSeasonAll(isOffSeasonAll);
    setAllMonthAll(isAllMonthAll);
    return {
      selectedDates: initialRangeDates,
      default: CUSTOM_LABEL,
      year: year,
      week: (isNcaa && isAllMonthAll) || (onlySeasonWeeks && isInSeasonAll) || (isInSeasonAll && isOffSeasonAll) ?
        ALL_SEASON_YEAR : CUSTOM_LABEL
    };
  }, [isFullYearRanges, filterData.selectedDates, filterData.year]);

  const handleYear = useCallback(async (year) => {
    setNewData({
      ...newData,
      year,
      selectedDates: getAllDateRanges(year, weeks, !!isNcaa, !onlySeasonWeeks),
      week: ALL_SEASON_YEAR
    });
    setInSeasonAll(true);
    setOffSeasonAll(true);
    setAllMonthAll(true);
  }, [newData]);

  const onClose = async () => {
    close();
  };

  const onOpen = async () => {
    open();
   if(changeToNfl){
      setChangeToNfl(false);
      setNewData(await getSelectedData(true));
    }else{
      if(changeToNcaa){
        setChangeToNcaa(false);
        setNewData(await getSelectedData(true));
      }else{
        if (weeks) {
          setNewData(await getSelectedData());
        }
      }
    }
  };

  const addWeekOrMonth = (item: any) => {
    let copyNewItems = [...newData.selectedDates];
    if (!isIn(newData.selectedDates, item)) {
      copyNewItems.push(item);
    } else {
      copyNewItems = copyNewItems.filter(newItem => JSON.stringify(newItem) !== JSON.stringify(item));
    }
    const [isInSeasonAll, isOffSeasonAll, isAllMonthAll] = isFullYearRanges(copyNewItems, newData.year);
    setInSeasonAll(isInSeasonAll);
    setOffSeasonAll(isOffSeasonAll);
    setAllMonthAll(isAllMonthAll);
    setNewData({
      ...newData,
      selectedDates: copyNewItems,
      week: ((isInSeasonAll && isOffSeasonAll) || isAllMonthAll) ? ALL_SEASON_YEAR : CUSTOM_LABEL
    });
  };
  const YEARS_REVERSE = [...YEARS].reverse();
  return (
    <Dropdown className="dropdown-date-compare" show={show} onToggle={show ? onClose : onOpen}>
      <Dropdown.Toggle
        id="dropdown-date-compare"
        className="btn-border"
        style={{ width: !isNcaa && !onlySeasonWeeks ? '190px' : '151px' }}
      >
        <label
          className={appliqued ? 'appliqued' : ''}
        >{isNcaa ? 'YEAR/MONTH' : onlySeasonWeeks ? 'SEASON/SEASON WEEK' : 'SEASON/SEASON WEEK/MONTH'}</label>
        {'' + filterData.week === '' + ALL_SEASON_YEAR ? (isNcaa ? filterData.year : filterData.year + '-' + (+filterData.year + 1)) : 'Custom'}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <div className={'season-week-container' + (!weeks ? ' loading' : '')}>
          <div className="season-week-body">
            <div className="season-week-column year column-border-right">
              {!onlySeasonWeeks && !isNcaa && <div className="head-title-column column-border-bottom">
                SEASON
              </div>}
              <div className="season-week-column-items">
                <form>
                  {YEARS_REVERSE.map((year, index) => (
                    <div
                      key={index}
                      className={'selector-season-item ' + (newData.year === year ? ' selected-label-week' : '')}
                    >
                      <label>
                        <div className="radio radio-week">
                          <input
                            id={'' + year} name="radio" type="radio" value={year}
                            checked={newData.year === year}
                            onChange={() => handleYear(year)}
                          />
                          <label htmlFor={'' + year} className="radio-label">
                            {isNcaa ?
                              year :
                              year + '-' + (year + 1)}
                          </label>
                        </div>
                      </label>
                    </div>
                  ))}
                </form>
              </div>
            </div>
            {isNcaa ?
              <div className="season-week-column off-season">
                <div className="season-week-select-all column-border-bottom">
                  Select All
                  <div>
                    <input
                      type="checkbox" id="select-all-off-season" name="set-name"
                      className="switch-input"
                      checked={allMonthAll}
                      onChange={() => {
                        if (allMonthAll) {
                          setNewData({
                            ...newData,
                            selectedDates: newData.selectedDates
                              .filter((range) => !(ALL_SEASON_MONTHS[+newData.year].some((month: Month) => (month.startOfMonth === range.start && month.endOfMonth === range.end))))
                          });
                          setAllMonthAll(false);
                        } else {
                          const ranges = [...newData.selectedDates];
                          for (const month of ALL_SEASON_MONTHS[+newData.year]) {
                            if (!ranges.some(range => (month.startOfMonth === range.start && month.endOfMonth === range.end))) {
                              ranges.push({ start: month.startOfMonth, end: month.endOfMonth });
                            }
                          }
                          setNewData({
                            ...newData,
                            selectedDates: ranges
                          });
                          setAllMonthAll(true);
                        }
                      }}
                    />
                    <label htmlFor="select-all-off-season" className="switch-label" />
                  </div>
                </div>
                <div className="season-week-column-items">
                  {(ALL_SEASON_MONTHS[+newData.year] || []).map((month: Month, index: number) => (
                    <div className="selector-season-item styled-checkbox-generic" key={index + month.startOfMonth}>
                      <input
                        id={(specificScope ? specificScope + '-' : '') + 'check-' + JSON.stringify(month)}
                        checked={isIn(newData.selectedDates, { start: month.startOfMonth, end: month.endOfMonth })}
                        type="checkbox"
                        value={month.startOfMonth}
                        onChange={() => {
                          addWeekOrMonth({ start: month.startOfMonth, end: month.endOfMonth });
                        }}
                      />
                      <label
                        htmlFor={(specificScope ? specificScope + '-' : '') + 'check-' + JSON.stringify(month)}
                        className="season-week-month-check"
                      >
                        {month.monthName}
                      </label>
                    </div>))}
                </div>
              </div> :
              <>
                <div className="season-week-column in-season  column-border-right">
                  {!onlySeasonWeeks && <div className="head-title-column column-border-bottom">
                    SEASON WEEK
                  </div>}
                  <div className="season-week-select-all column-border-bottom">
                    Select All
                    <div>
                      <input
                        type="checkbox" id="select-all-in-season" name="set-name"
                        className="switch-input"
                        checked={inSeasonAll}
                        onChange={() => {
                          if (inSeasonAll) {
                            setNewData({
                              ...newData,
                              selectedDates: newData.selectedDates
                                .filter((range) => !(weeks[newData.year].some((week: Week) => (week.startOfWeek === range.start && week.endOfWeek === range.end))))
                            });
                            setInSeasonAll(false);
                          } else {
                            const ranges = [...newData.selectedDates];
                            for (const week of weeks[newData.year]) {
                              if (!ranges.some(range => (week.startOfWeek === range.start && week.endOfWeek === range.end))) {
                                ranges.push({ start: week.startOfWeek, end: week.endOfWeek });
                              }
                            }
                            setNewData({
                              ...newData,
                              selectedDates: ranges
                            });
                            setInSeasonAll(true);
                          }
                        }}
                      />
                      <label htmlFor="select-all-in-season" className="switch-label" />
                    </div>
                  </div>
                  <div className="season-week-column-items">
                    {((weeks && weeks[newData.year]) || []).map((week: Week, index: number) => (
                      <div className="selector-season-item styled-checkbox-generic" key={index + week.startOfWeek}>
                        <input
                          id={(specificScope ? specificScope + '-' : '') + 'check-' + JSON.stringify(week)}
                          checked={isIn(newData.selectedDates, { start: week.startOfWeek, end: week.endOfWeek })}
                          type="checkbox"
                          value={week.startOfWeek}
                          onChange={() => {
                            addWeekOrMonth({ start: week.startOfWeek, end: week.endOfWeek });
                          }}
                        />
                        <label
                          htmlFor={(specificScope ? specificScope + '-' : '') + 'check-' + JSON.stringify(week)}
                          className="season-week-month-check"
                        >{week.weekName}</label>
                      </div>
                    ))}
                  </div>
                </div>
                {!onlySeasonWeeks && <div className="season-week-column off-season">
                  <div className="head-title-column column-border-bottom">
                    OFF-SEASON MONTHS
                  </div>
                  <div className="season-week-select-all column-border-bottom">
                    Select All
                    <div>
                      <input
                        type="checkbox" id="select-all-off-season" name="set-name"
                        className="switch-input"
                        checked={offSeasonAll}
                        onChange={() => {
                          if (offSeasonAll) {
                            setNewData({
                              ...newData,
                              selectedDates: newData.selectedDates
                                .filter((range) => !(OFF_SEASON_MONTHS[+newData.year].some((week: Month) => (week.startOfMonth === range.start && week.endOfMonth === range.end))))
                            });
                            setOffSeasonAll(false);
                          } else {
                            const ranges = [...newData.selectedDates];
                            for (const week of OFF_SEASON_MONTHS[+newData.year]) {
                              if (!ranges.some(range => (week.startOfMonth === range.start && week.endOfMonth === range.end))) {
                                ranges.push({ start: week.startOfMonth, end: week.endOfMonth });
                              }
                            }
                            setNewData({
                              ...newData,
                              selectedDates: ranges
                            });
                            setOffSeasonAll(true);
                          }
                        }}
                      />
                      <label htmlFor="select-all-off-season" className="switch-label" />
                    </div>
                  </div>
                  <div className="season-week-column-items">
                    {(OFF_SEASON_MONTHS[+newData.year] || []).map((month: Month, index: number) => (
                      <div className="selector-season-item styled-checkbox-generic" key={index + month.startOfMonth}>
                        <input
                          id={(specificScope ? specificScope + '-' : '') + 'check-' + JSON.stringify(month)}
                          checked={isIn(newData.selectedDates, { start: month.startOfMonth, end: month.endOfMonth })}
                          type="checkbox"
                          value={month.startOfMonth}
                          onChange={() => {
                            addWeekOrMonth({ start: month.startOfMonth, end: month.endOfMonth });
                          }}
                        />
                        <label
                          htmlFor={(specificScope ? specificScope + '-' : '') + 'check-' + JSON.stringify(month)}
                          className="season-week-month-check"
                        >
                          {month.monthName}
                        </label>
                      </div>))}
                  </div>
                </div>}
              </>}
          </div>
          <div className="footer-s">
            <button className="btn-transparet" onClick={onClose}>Cancel
            </button>
            <button
              disabled={+newData.selectedDates.length <= +0}
              className={'btn-blue ' + (+newData.selectedDates.length === 0 ? 'opacity-50' : '')}
              onClick={() => {
                close();
                setAppliqued(true);
                changeFilterDataWrapped({
                  ...newData,
                  selectedDates: newData.selectedDates.map((range: RangeYYMMDDDDate) => ({
                    start: YYYYMMDDToDate(range.start),
                    end: YYYYMMDDToDate(range.end)
                  })),
                  default: CUSTOM_LABEL,
                  year: newData.year,
                  week: (isNcaa && allMonthAll) || (onlySeasonWeeks && inSeasonAll) || (inSeasonAll && offSeasonAll) ?
                    ALL_SEASON_YEAR : CUSTOM_LABEL
                })();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>);
};

export const YearSeasonFilter = (props: YearSeasonFilterPropsType) => {
  const { [FILTER_GLOBAL_KEYS.WEEKS]: weeks = {} } = useFiltersState('_', FILTER_GLOBAL_KEYS.WEEKS);
  const { search } = useRouter();
  const urlSearchParams = new URLSearchParams(search);
  const currentTeam = urlSearchParams.get(FILTER_KEYS.TEAM_ID);
  const currentTeamData = getTeamData(currentTeam as string);
  const newProps = {...props, isNcaa: currentTeamData? currentTeamData.isNCAA : props.isNcaa};
  return (
    <ParamWrapper
      fromUrlSearchParam={(cad, props) => {
        const param = isJson(cad) ? JSON.parse(cad) : null;
        const year = (param && param.year) ? param.year : (props.defaultYear ? props.defaultYear : getCurrentSeasonYear(!!props.isNcaa));
        const allSelectedDates = getAllDateRanges(year, weeks, !!props.isNcaa, !props.onlySeasonWeeks);
        if (param) {
          return {
            selectedDates: (param && param.week) === ALL_SEASON_YEAR ? allSelectedDates.map(range => ({
              start: YYYYMMDDToDate(range.start),
              end: YYYYMMDDToDate(range.end)
            })) : allSelectedDates.map(range => ({
              start: YYYYMMDDToDate(range.start),
              end: YYYYMMDDToDate(range.end)
            })).filter(range => isIn(param.selectedDates, range)),
            default: param.default || CUSTOM_LABEL,
            year: param.year || '',
            week: param.week || CUSTOM_LABEL
          };
        }
        if (weeks) {
          const isInSeasonAll = ((weeks && weeks[year]) || []).every((week: Week) => (isIn(allSelectedDates, {
            start: week.startOfWeek,
            end: week.endOfWeek
          })));
          const isOffSeasonAll = (OFF_SEASON_MONTHS[year] || []).every((month) => (isIn(allSelectedDates, {
            start: month.startOfMonth,
            end: month.endOfMonth
          })));
          const isAllMonthAll = (ALL_SEASON_MONTHS[year] || []).every((month) => (isIn(allSelectedDates, {
            start: month.startOfMonth,
            end: month.endOfMonth
          })));
          return {
            selectedDates: allSelectedDates.map(range => ({
              start: YYYYMMDDToDate(range.start),
              end: YYYYMMDDToDate(range.end)
            })),
            default: CUSTOM_LABEL,
            year,
            week: (props.isNcaa && isAllMonthAll) || (props.onlySeasonWeeks && isInSeasonAll) || (isInSeasonAll && isOffSeasonAll) ?
              ALL_SEASON_YEAR : CUSTOM_LABEL
          };
        }

        return {
          selectedDates: [],
          default: CUSTOM_LABEL,
          year: '',
          week: CUSTOM_LABEL
        };
      }}
      toUrlSearchParam={selectedData => {
        if (selectedData) {
          return JSON.stringify(selectedData);
        }
        return '';
      }}
      Component={YearSeasonFilterWrapped}
      props={newProps}
      filterKey={FILTER_KEYS.DATE_RANGE}
    />
  );
};
